// src/components/TeachingPresentation.js
import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { TEACHING_CONTENT } from '../data/teachingContent';

const TeachingPresentation = ({ factorNumber, onClose, initialSlide = 0 }) => {
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const factor = TEACHING_CONTENT[factorNumber];

  // Total slides: 8 slides per factor
  const totalSlides = factor.slides.length;

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowRight') {
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Home') {
        setCurrentSlide(0);
      } else if (e.key === 'End') {
        setCurrentSlide(totalSlides - 1);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlide, totalSlides]);

  const nextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const renderTitleSlide = (slide) => (
    <div className="h-full flex flex-col items-center justify-center p-4 sm:p-8 md:p-12 text-center">
      <div className="mb-4 sm:mb-6 md:mb-8">
        <span className="text-4xl sm:text-5xl md:text-6xl">📚</span>
      </div>
      <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 md:mb-8 leading-tight max-w-5xl px-2">
        {slide.title}
      </h1>
      <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold mb-6 sm:mb-8 md:mb-12 text-yellow-400 px-2">
        {factor.factorName}
      </h2>
      <div className="max-w-4xl px-4">
        <p className="text-base sm:text-lg md:text-xl lg:text-2xl italic leading-relaxed mb-4 sm:mb-6 md:mb-8 opacity-90">
          {slide.scripture}
        </p>
        {slide.highlight && (
          <div className="mt-4 sm:mt-6 md:mt-8 bg-white/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8">
            <p className="text-sm sm:text-base md:text-lg lg:text-2xl font-medium leading-relaxed">
              {slide.highlight}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderContentSlide = (slide) => (
    <div className="h-full flex flex-col justify-center p-4 sm:p-6 md:p-8 lg:p-12">
      <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8 md:mb-12 text-center leading-tight px-2">
        {slide.title}
      </h2>
      <div className="max-w-5xl mx-auto space-y-3 sm:space-y-4 md:space-y-6">
        {slide.points.map((point, index) => (
          <div
            key={index}
            className="bg-white/10 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 backdrop-blur-sm"
          >
            <p className="text-sm sm:text-base md:text-lg lg:text-2xl leading-relaxed">
              {point}
            </p>
          </div>
        ))}
      </div>
      {slide.scripture && (
        <div className="mt-6 sm:mt-8 md:mt-12 text-center px-4">
          <p className="text-sm sm:text-base md:text-lg lg:text-xl italic opacity-90">
            {slide.scripture}
          </p>
        </div>
      )}
    </div>
  );

  const renderReflectionSlide = (slide) => (
    <div className="h-full flex flex-col items-center justify-center p-4 sm:p-6 md:p-8 lg:p-12 text-center">
      <div className="mb-4 sm:mb-6 md:mb-8">
        <span className="text-4xl sm:text-5xl md:text-6xl">💭</span>
      </div>
      <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8 md:mb-12 leading-tight px-2">
        {slide.title}
      </h2>
      <div className="max-w-4xl space-y-4 sm:space-y-6 md:space-y-8 px-4">
        {slide.questions.map((question, index) => (
          <div
            key={index}
            className="bg-white/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 backdrop-blur-sm"
          >
            <p className="text-sm sm:text-base md:text-lg lg:text-2xl leading-relaxed font-medium">
              {question}
            </p>
          </div>
        ))}
      </div>
      {slide.instruction && (
        <div className="mt-6 sm:mt-8 md:mt-12 text-sm sm:text-base md:text-lg lg:text-xl opacity-90 max-w-3xl px-4">
          {slide.instruction}
        </div>
      )}
    </div>
  );

  const renderActionSlide = (slide) => (
    <div className="h-full flex flex-col justify-center p-4 sm:p-6 md:p-8 lg:p-12">
      <div className="text-center mb-4 sm:mb-6 md:mb-8">
        <span className="text-4xl sm:text-5xl md:text-6xl">✨</span>
      </div>
      <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8 md:mb-12 text-center leading-tight px-2">
        {slide.title}
      </h2>
      <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6 md:space-y-8 px-4">
        {slide.challenge && (
          <div className="bg-yellow-400/20 border-2 border-yellow-400 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 md:mb-4 text-yellow-300">
              🎯 This Week's Challenge:
            </h3>
            <p className="text-sm sm:text-base md:text-lg lg:text-2xl leading-relaxed">
              {slide.challenge}
            </p>
          </div>
        )}
        {slide.nextStep && (
          <div className="bg-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 md:mb-4 text-blue-300">
              📋 Next Steps:
            </h3>
            <p className="text-sm sm:text-base md:text-lg lg:text-2xl leading-relaxed">
              {slide.nextStep}
            </p>
          </div>
        )}
        {slide.scripture && (
          <div className="text-center mt-4 sm:mt-6 md:mt-8">
            <p className="text-sm sm:text-base md:text-lg lg:text-xl italic opacity-90">
              {slide.scripture}
            </p>
          </div>
        )}
        {slide.closing && (
          <div className="text-center mt-4 sm:mt-6 md:mt-8 bg-white/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6">
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-medium italic">
              {slide.closing}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderSlide = () => {
    const slide = factor.slides[currentSlide];
    
    switch (slide.type) {
      case 'title':
        return renderTitleSlide(slide);
      case 'content':
        return renderContentSlide(slide);
      case 'reflection':
        return renderReflectionSlide(slide);
      case 'action':
        return renderActionSlide(slide);
      default:
        return renderContentSlide(slide);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 text-white">
      {/* Main Content - Add padding bottom for control bar */}
      <div className="h-full w-full overflow-y-auto pb-16 sm:pb-20 md:pb-24">
        {renderSlide()}
      </div>

      {/* Control Bar - Responsive */}
      <div className="fixed bottom-0 left-0 right-0 bg-black/40 backdrop-blur-md flex items-center justify-between px-2 sm:px-4 md:px-8 py-2 sm:py-3 md:py-4">
        {/* Left: Navigation */}
        <div className="flex items-center gap-1 sm:gap-2 md:gap-4">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg transition-all text-xs sm:text-sm md:text-base ${
              currentSlide === 0
                ? 'bg-white/10 text-white/30 cursor-not-allowed'
                : 'bg-white/20 hover:bg-white/30 text-white'
            }`}
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline font-medium">Previous</span>
            <span className="sm:hidden font-medium">Prev</span>
          </button>
          
          <button
            onClick={nextSlide}
            disabled={currentSlide === totalSlides - 1}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg transition-all text-xs sm:text-sm md:text-base ${
              currentSlide === totalSlides - 1
                ? 'bg-white/10 text-white/30 cursor-not-allowed'
                : 'bg-white/20 hover:bg-white/30 text-white'
            }`}
          >
            <span className="font-medium">Next</span>
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          
          <div className="ml-1 sm:ml-2 md:ml-4 text-sm sm:text-base md:text-lg font-medium">
            {currentSlide + 1}/{totalSlides}
          </div>
        </div>

        {/* Center: Mode Indicator - Hidden on very small screens */}
        <div className="hidden md:flex items-center gap-3">
          <span className="text-2xl">📚</span>
          <div className="text-center">
            <div className="text-sm opacity-75">Teaching Mode</div>
            <div className="text-lg font-semibold">Factor {factorNumber}</div>
          </div>
        </div>

        {/* Right: Exit Button */}
        <button
          onClick={onClose}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-all text-xs sm:text-sm md:text-base"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="hidden sm:inline font-medium">Exit</span>
        </button>
      </div>

      {/* Keyboard Shortcuts Hint - Hidden on mobile, shown on desktop */}
      <div className="hidden lg:block fixed top-4 right-4 bg-black/40 backdrop-blur-md rounded-lg px-4 py-2 text-sm opacity-75">
        <div className="flex items-center gap-4">
          <span>← → Navigate</span>
          <span>Home First</span>
          <span>End Last</span>
          <span>ESC Exit</span>
        </div>
      </div>
    </div>
  );
};

export default TeachingPresentation;